import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Flag, FilePdf, UploadSimple, Trash, CheckCircle, User, Package,
  ClipboardText, MapPin, TestTube, PencilSimple
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pendente: { label: "Pendente", className: "bg-amber-100 text-amber-700 border-amber-200" },
  em_analise: { label: "Em Análise", className: "bg-sky-100 text-sky-700 border-sky-200" },
  resolvida: { label: "Resolvida", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const DOC_TYPE_LABELS = { factura: "Factura", vale_dinheiro: "Venda a Dinheiro", guia_remessa: "Guia Remessa" };

const Section = ({ icon: Icon, title, children }) => (
  <div className="gresart-card">
    <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#009392]" />
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-stone-400 mb-0.5">{label}</p>
    <p className="text-sm text-stone-800 font-medium whitespace-pre-wrap">{value || "-"}</p>
  </div>
);

export default function ReclamacaoDetail() {
  const { complaintId } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [solutionText, setSolutionText] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const fetchComplaint = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/complaints/${complaintId}`);
      setComplaint(response.data);
    } catch (error) {
      console.error("Error fetching complaint:", error);
      toast.error("Reclamação não encontrada");
    } finally {
      setLoading(false);
    }
  }, [complaintId]);

  useEffect(() => { fetchComplaint(); }, [fetchComplaint]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/complaints/${complaintId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reclamacao_${(complaint?.client_name || 'Cliente').replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro ao gerar o PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/complaints/${complaintId}/photos/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await fetchComplaint();
      toast.success(`${files.length} foto(s) anexada(s)`);
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Erro ao anexar foto");
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/complaints/${complaintId}/photos/${photoId}`);
      await fetchComplaint();
      toast.success("Foto removida");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Erro ao remover foto");
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSavingStatus(true);
    try {
      const response = await axios.patch(`${API}/complaints/${complaintId}/status`, { status: newStatus });
      setComplaint(response.data);
      toast.success("Estado atualizado");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erro ao atualizar estado");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleResolve = async () => {
    if (!solutionText.trim()) {
      toast.error("Descreva a solução aplicada");
      return;
    }
    setSavingStatus(true);
    try {
      const response = await axios.patch(`${API}/complaints/${complaintId}/status`, { status: "resolvida", solution: solutionText });
      setComplaint(response.data);
      setShowResolveDialog(false);
      toast.success("Reclamação marcada como resolvida");
    } catch (error) {
      console.error("Error resolving complaint:", error);
      toast.error("Erro ao marcar como resolvida");
    } finally {
      setSavingStatus(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString('pt-PT'); } catch { return "-"; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="text-center py-16">
        <p className="text-stone-500">Reclamação não encontrada</p>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[complaint.status] || STATUS_CONFIG.pendente;
  const appLocation = [complaint.application_environment, complaint.application_surface].filter(Boolean).join(" - ");

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="reclamacao-detail-page">
      <PageHeader
        icon={Flag}
        title={complaint.client_name || "Reclamação"}
        subtitle={`Registada em ${formatDate(complaint.created_at)} por ${complaint.reseller_name || '-'}`}
        onBack={() => navigate('/reclamacoes')}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/reclamacoes/${complaintId}/editar`)} data-testid="btn-edit-complaint">
              <PencilSimple size={18} />
              Editar
            </Button>
            <Button variant="outline" onClick={handleDownloadPdf} disabled={downloadingPdf} data-testid="btn-download-pdf">
              <FilePdf size={18} />
              {downloadingPdf ? "A gerar..." : "Descarregar PDF"}
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border ${statusCfg.className}`} data-testid="complaint-current-status">
          {statusCfg.label}
        </span>
        {complaint.status !== "resolvida" && (
          <Select value={complaint.status} onValueChange={(v) => v === 'resolvida' ? setShowResolveDialog(true) : handleStatusChange(v)} disabled={savingStatus}>
            <SelectTrigger className="w-44" data-testid="select-change-status">
              <SelectValue placeholder="Alterar estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="em_analise">Em Análise</SelectItem>
              <SelectItem value="resolvida">Marcar como Resolvida</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {complaint.status === "resolvida" && (
        <div className="gresart-card border-emerald-200 bg-emerald-50/50" data-testid="complaint-solution-box">
          <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wide flex items-center gap-2 mb-2">
            <CheckCircle size={16} weight="fill" />
            Solução Aplicada
          </h3>
          <p className="text-sm text-stone-800 whitespace-pre-wrap">{complaint.solution || "-"}</p>
          <p className="text-xs text-stone-400 mt-2">Resolvida em {formatDate(complaint.resolved_at)} por {complaint.resolved_by || '-'}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={User} title="Dados do Cliente">
          <div className="space-y-3">
            <Field label="Nome" value={complaint.client_name} />
            <Field label="Morada" value={complaint.client_address} />
            <Field label="Contacto" value={complaint.client_contact} />
            <Field label="Horário de Disponibilidade" value={complaint.client_contact_schedule} />
          </div>
        </Section>

        <Section icon={ClipboardText} title="Documento de Fornecimento">
          <div className="space-y-3">
            <Field label="Documento de Fornecimento" value={DOC_TYPE_LABELS[complaint.supply_doc_type] || "-"} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Número do Documento" value={complaint.supply_doc_number} />
              <Field label="Data do Documento" value={complaint.supply_doc_date} />
            </div>
          </div>
        </Section>

        <Section icon={Flag} title="Descrição da Reclamação">
          <div className="space-y-3">
            <Field label="Descrição / Tipo de Defeito" value={complaint.complaint_description} />
            <Field label="Observações Adicionais" value={complaint.observations} />
          </div>
        </Section>

        <Section icon={Package} title="Identificação do Produto">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Referência" value={complaint.product_reference} />
              <Field label="Nome do Produto" value={complaint.product_name} />
            </div>
            <Field label="Quantidade (m²)" value={complaint.product_quantity_m2} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Controlo" value={complaint.control} />
              <Field label="Qualidade" value={complaint.quality_grade} />
              <Field label="Calibre" value={complaint.caliber} />
              <Field label="Tonalidade" value={complaint.tonality} />
            </div>
          </div>
        </Section>

        <Section icon={MapPin} title="Morada da Obra">
          <div className="space-y-3">
            <Field label="Morada da Obra" value={complaint.work_address} />
            <Field label="Contacto do Responsável da Obra" value={complaint.work_contact} />
          </div>
        </Section>

        <Section icon={MapPin} title="Aplicação do Produto">
          <div className="space-y-3">
            <Field label="Local de Aplicação" value={appLocation} />
            <Field label="Data de Aplicação" value={complaint.application_date} />
          </div>
        </Section>

        <Section icon={TestTube} title="Amostras Recolhidas">
          <div className="space-y-3">
            <Field label="Tipo de Amostra" value={complaint.samples_type} />
            <Field label="Local de Recolha" value={complaint.samples_location} />
          </div>
        </Section>

        <Section icon={MapPin} title="Visita à Obra">
          <div className="space-y-3">
            <Field label="Houve Visita à Obra?" value={complaint.work_visit_done ? "Sim" : "Não"} />
            {complaint.work_visit_done && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Data da Visita" value={complaint.work_visit_date} />
                <Field label="Hora da Visita" value={complaint.work_visit_time} />
              </div>
            )}
          </div>
        </Section>

        <Section icon={ClipboardText} title="Parecer do Vendedor">
          <Field label="Parecer do Vendedor" value={complaint.seller_opinion} />
        </Section>

        <Section icon={ClipboardText} title="Sugestões do Cliente">
          <Field label="Sugestões do Cliente" value={complaint.improvement_suggestions} />
        </Section>
      </div>

      <div className="gresart-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide">Fotografias</h3>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#009392] hover:text-[#007a7a]">
              <UploadSimple size={16} />
              {uploadingPhoto ? "A carregar..." : "Anexar Fotos"}
            </span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} data-testid="detail-photo-input" />
          </label>
        </div>
        {(complaint.photos || []).length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-6">Sem fotografias anexadas</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="detail-photos-grid">
            {complaint.photos.map(photo => (
              <div key={photo.id} className="relative group">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`} alt={photo.name} className="w-full h-28 object-cover rounded-lg border border-stone-200" />
                <button
                  type="button"
                  onClick={() => handlePhotoDelete(photo.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`detail-photo-delete-${photo.id}`}
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle>Marcar como Resolvida</DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-sm text-stone-500 mb-2">Descreva a solução aplicada a esta reclamação:</p>
            <Textarea
              value={solutionText}
              onChange={(e) => setSolutionText(e.target.value)}
              placeholder="Ex: Substituição do material e reembolso parcial"
              rows={4}
              data-testid="resolve-solution-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>Cancelar</Button>
            <Button onClick={handleResolve} disabled={savingStatus} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="confirm-resolve-btn">
              {savingStatus ? "A guardar..." : "Confirmar Resolução"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
